import React, { useState, useEffect, useContext } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import CountryField from '../inputs/CountryField';
import RegionField from '../inputs/RegionField';
import CardWrapper from '../cardWrapper';
import CardCvc from '../inputs/cardCvc';
import TextField from '../inputs/TextField';
import PhoneField from '../inputs/PhoneField';
import Section from '../Section';
import Checkbox from '../inputs/Checkbox';
import { validationSchema } from '../../func/validation';
import scrollIntoView from '../../func/scrollIntoView';
import Button from '../buttons/Button';
import CardField from '../inputs/cardField';
import PaypalButton from '../buttons/paypalButton';
import questionIcon from '/fallbacks/question.svg';
import lockIcon from '/fallbacks/lock-2.svg';
import PaymentsIcon from '/fallbacks/paymentColor.svg';
import PaymentsIconColored from '/fallbacks/secure-payment-logos-color.svg';
import LockSvg from '/fallbacks/lock.svg';
import shieldIcon from '/fallbacks/shield.svg';
import PersonIcon from '/fallbacks/person-profile.svg';
import DeliveryIcon from '/fallbacks/fast-delivery.svg';
import Expiration from '../inputs/Expiration';
import { TemplateContext } from '../../layout';

const FormClassic = ({
	handleFormSubmit,
	labels,
	headings,
	formSubmited,
	initialValues,
	cleanVersion,
	handlePaypalSubmit,
	compliance,
}) => {
	const [country, setCountry] = useState('');
	const [templateContext, setTemplateContext] = useContext(TemplateContext);
	const directAffiliate = window?.affiliate.includes('-direct');
	const showTermsBox = cleanVersion || compliance || directAffiliate;

	const [submitType, setSubmitType] = useState('card');
	const base = window.post_template?.checkout_v3_form;
	const sslText = base.tr_checkout_v3_formEncrypted;

	const paymentsColoredIcon = base.checkout_v3_paymentsAfter ? base.checkout_v3_paymentsAfter : PaymentsIconColored;
	const paymentMethodsSectionIcon = headings.checkout_v3_form_methodsicon
		? headings.checkout_v3_form_methodsicon
		: shieldIcon;
	const cvcIcon =
		labels.checkout_v3_form_securityCom && labels.tr_checkout_v3_form_securityIcon
			? labels.tr_checkout_v3_form_securityIcon
			: questionIcon;
	const informationSectionIcon = headings.checkout_v3_form_informationImage
		? headings.checkout_v3_form_informationImage
		: DeliveryIcon;
	const shippingSectionIcon = headings.checkout_v3_form_shippingIcon
		? headings.checkout_v3_form_shippingIcon
		: PersonIcon;

	const {
		register,
		handleSubmit,
		setValue,
		control,
		trigger,
		watch,
		getValues,
		formState: { errors, submitCount },
	} = useForm({
		defaultValues: initialValues,
		resolver: yupResolver(validationSchema),
		context: { type: { method: submitType, safe: !!showTermsBox } },
	});

	useEffect(() => {
		scrollIntoView(errors);
	}, [errors]);

	useEffect(() => {
		const { country } = getValues();
		if (country) {
			setCountry(country);
		}
	}, []);

	const expiration = watch(['expiration']);

	useEffect(() => {
		if (expiration[0] && expiration[0].length > 3) {
			const splitedValue = expiration[0].split('/');
			splitedValue[0] && setValue('card_exp_month', splitedValue[0]);
			splitedValue[1] && setValue('card_exp_year', `20${splitedValue[1]}`);
		}
	}, [expiration]);

	return (
		<form
			autocomplete="on"
			onSubmit={handleSubmit((data) => {
				handleFormSubmit(data, templateContext);
			})}
		>
			<div className="paymentMethodClassic formSubmit">
				<Section
					paddings={false}
					withBorder={false}
					narrowGap={true}
					heading={headings.checkout_v3_form_methodsHeading}
					icon={paymentMethodsSectionIcon}
				>
					<div style={{ marginTop: '-16px', width: '100%' }}>
						<PaypalButton
							type="button"
							text={base?.tr_checkout_v3_form_paypalText}
							handleClick={async (e) => {
								e.preventDefault();
								if (cleanVersion) {
									const isValid = await trigger('policy');
									if (isValid) {
										setTemplateContext({ ...templateContext, paymentMethod: 'paypal' });
										await handlePaypalSubmit();
									}
								} else {
									setTemplateContext({ ...templateContext, paymentMethod: 'paypal' });
									handlePaypalSubmit();
								}
							}}
						></PaypalButton>
					</div>
				</Section>
				<div className="paymentMethodClassic__seperator">
					<div className="paymentMethodClassic__seperatorLine"></div>
					<div className="paymentMethodClassic__seperatorText">{base?.tr_checkout_v3_form_widthCard}</div>
					<div className="paymentMethodClassic__seperatorLine"></div>
				</div>
				<CardWrapper heading={base?.tr_checkout_v3_form_card_details_label} icon={PaymentsIcon}>
					<Controller
						control={control}
						name="card_number"
						render={({ field: { onChange, value } }) => (
							<CardField
								name="card_number"
								icon={lockIcon}
								comment={labels.tr_checkout_v3_form_cardComment}
								width="full"
								placeholder={labels.tr_checkout_v3_form_cardNumberLabel}
								error={errors.card_number ? errors.card_number.message : null}
								value={value}
								handleChange={onChange}
								submitCount={submitCount}
							/>
						)}
					/>
					<Controller
						control={control}
						name="expiration"
						render={({ field: { onChange, value } }) => (
							<Expiration
								type="tel"
								name="expiration"
								width={67}
								max={5}
								placeholder={labels.tr_checkout_v3_form_expirationLabel}
								margin={true}
								error={
									errors.card_exp_year || errors.card_exp_month
										? errors.card_exp_year?.message || errors.card_exp_month?.message
										: null
								}
								dataCy="expiration-input"
								register={register}
								value={value}
								trigger={trigger}
								handleChange={onChange}
							/>
						)}
					/>
					<Controller
						control={control}
						name="card_cvc"
						render={({ field: { onChange, value } }) => (
							<CardCvc
								type="tel"
								max={9999}
								min={1}
								name="card_cvc"
								icon={cvcIcon}
								comment={labels.checkout_v3_form_securityCom && labels.tr_checkout_v3_form_securityComment}
								width={33}
								submitCount={submitCount}
								placeholder={labels.tr_checkout_v3_form_securityLabel}
								error={errors.card_cvc?.message ? errors.card_cvc?.message : null}
								value={value}
								setValue={setValue}
								handleChange={onChange}
							/>
						)}
					/>
				</CardWrapper>
				<Section
					mobileBorders={true}
					paddings={false}
					withBorder={false}
					narrowGap={true}
					heading={headings.tr_checkout_v3_form_informationHeading}
					icon={informationSectionIcon}
				>
					<Controller
						control={control}
						name="email"
						render={({ field: { onChange, value } }) => (
							<TextField
								type="email"
								placeholder={labels.tr_checkout_v3_form_emailLabel}
								name="email"
								width="full"
								autoComplete="email"
								error={errors.email ? errors.email.message : null}
								value={value}
								handleChange={onChange}
							/>
						)}
					/>
					<Controller
						control={control}
						name="phone"
						render={({ field: { onChange, value } }) => (
							<PhoneField
								value={value}
								error={errors.phone ? errors.phone.message : null}
								handleChange={onChange}
							/>
						)}
					></Controller>
				</Section>
				<Section
					paddings={false}
					withBorder={false}
					mobileBorders={true}
					narrowGap={true}
					heading={headings.tr_checkout_v3_form_shippingHeading}
					icon={shippingSectionIcon}
					iconOffset={-1}
				>
					<Controller
						control={control}
						name="first_name"
						render={({ field: { onChange, value } }) => (
							<TextField
								type="text"
								placeholder={labels.tr_checkout_v3_form_nameLabel}
								name="first_name"
								width="half"
								margin="true"
								autoComplete="cc-given_name"
								value={value}
								handleChange={onChange}
								error={errors.first_name ? errors.first_name?.message : null}
							/>
						)}
					/>
					<Controller
						control={control}
						name="last_name"
						render={({ field: { onChange, value } }) => (
							<TextField
								type="text"
								placeholder={labels.tr_checkout_v3_form_lastNameLabel}
								name="last_name"
								autoComplete="cc-family_name"
								register={register}
								error={errors.last_name ? errors.last_name?.message : null}
								width="half"
								setValue={setValue}
								value={value}
								handleChange={onChange}
							/>
						)}
					/>
					<Controller
						control={control}
						name="address"
						render={({ field: { onChange, value } }) => (
							<TextField
								type="text"
								placeholder={labels.tr_checkout_v3_form_addressLabel}
								error={errors.address ? errors.address.message : null}
								name="address"
								width="full"
								autoComplete="street-address"
								register={register}
								setValue={setValue}
								value={value}
								handleChange={onChange}
							/>
						)}
					/>
					<Controller
						control={control}
						name="country"
						render={({ field: { onChange, value } }) => (
							<CountryField
								label={labels.tr_checkout_v3_form_countryLabel}
								name="country"
								error={errors.country ? errors.country.message : null}
								value={value}
								handleChange={(e) => {
									onChange(e);
									setCountry(e);
								}}
							/>
						)}
					/>

					<Controller
						control={control}
						name="city"
						render={({ field: { onChange, value } }) => (
							<TextField
								type="text"
								placeholder={labels.tr_checkout_v3_form_cityLabel}
								name="city"
								width="half"
								autoComplete="country-name"
								error={errors.city ? errors.city.message : null}
								setValue={setValue}
								register={register}
								value={value}
								handleChange={onChange}
							/>
						)}
					/>
					<Controller
						control={control}
						name="state"
						render={({ field: { onChange, value } }) => (
							<RegionField
								label={labels.tr_checkout_v3_form_stateLabel}
								error={errors.state ? errors.state.message : null}
								country={country || window.countryCode.toUpperCase()}
								handleChange={onChange}
								value={value}
								defaultLabel={base?.tr_checkout_v3_form_select_region_label}
							/>
						)}
					/>
					<Controller
						control={control}
						name="zip"
						render={({ field: { onChange, value } }) => (
							<TextField
								type="text"
								placeholder={labels.tr_checkout_v3_form_postLabel}
								error={errors.zip ? errors.zip.message : null}
								name="zip"
								autoComplete="postal-code"
								setValue={setValue}
								width="half"
								register={register}
								value={value}
								handleChange={onChange}
							/>
						)}
					/>
				</Section>

				{showTermsBox && (
					<div style={{ marginBottom: '16px' }}>
						<Controller
							control={control}
							name="policy"
							render={({ field: { onChange, value } }) => (
								<Checkbox
									error={errors.policy ? errors.policy.message : null}
									value={value}
									handleChange={onChange}
									name="policy"
								/>
							)}
						/>
					</div>
				)}

				<Button
					margin={cleanVersion ? 16 : 4}
					type="submit"
					text={base?.tr_checkout_v3_form_cta}
					processingLabel={base?.tr_checkout_v3_form_processing}
					handleChange={() => {
						setSubmitType('card');
					}}
					loading={formSubmited}
				></Button>
				<div className="paymentIcons">
					<div className="paymentIcons__text">
						<span style={{ backgroundImage: `url('${LockSvg}')` }}></span>
						<span>{sslText}</span>
					</div>
					<img className="paymentIcons__icons" src={`${paymentsColoredIcon}`} alt="Payments" />
				</div>
			</div>
		</form>
	);
};

export default FormClassic;
